/**
 * notificationPresentation.ts
 *
 * Pure presentation helpers for the on-demand shipment notification history drawer.
 * No side effects — safe to import everywhere.
 */

import type {
  NotificationDeliveryStatus,
  DispatchOutcome,
  NotificationChannel,
  NotificationHistoryItem,
} from '../../../api/types';

// ─── Status Labels ────────────────────────────────────────────────────────────

export interface StatusLabelResult {
  label: string;
  /** CSS color token or hex */
  color: string;
  /** Background tint for badge */
  bgColor: string;
}

/**
 * Returns a human-readable label and badge color for a notification entry,
 * combining delivery status and dispatch outcome per the RFC §9 spec.
 *
 * IMPORTANT: Do NOT derive vendor interest or call-to-action from read timestamps.
 * Read receipts are informational only.
 */
export function notificationLabel(
  status: NotificationDeliveryStatus | undefined,
  dispatchOutcome: DispatchOutcome | null | undefined,
): StatusLabelResult {
  // Terminal delivery states take precedence
  if (status === 'READ') {
    return { label: 'Read', color: 'var(--status-green-text-2)', bgColor: 'var(--status-green-bg-2)' };
  }
  if (status === 'DELIVERED') {
    return { label: 'Delivered', color: 'var(--status-green-text-2)', bgColor: 'var(--status-green-bg)' };
  }
  if (status === 'SKIPPED_ALLOWLIST') {
    return {
      label: 'Skipped (Allowlist)',
      color: 'var(--muted)',
      bgColor: 'var(--status-gray-bg)',
    };
  }
  if (status === 'SKIPPED_COOLDOWN') {
    return {
      label: 'Skipped (Cooldown)',
      color: 'var(--muted)',
      bgColor: 'var(--status-gray-bg)',
    };
  }
  if (status === 'CANCELLED') {
    return { label: 'Cancelled', color: 'var(--muted)', bgColor: 'var(--status-gray-bg)' };
  }

  // Sent — qualify with dispatch outcome
  if (status === 'SENT') {
    if (dispatchOutcome === 'ACCEPTED') {
      return {
        label: 'Accepted by provider',
        color: 'var(--accent)',
        bgColor: 'var(--action-primary-soft)',
      };
    }
    if (dispatchOutcome === 'DEFINITIVE_FAILURE') {
      return {
        label: 'Provider rejected',
        color: 'var(--status-red-text)',
        bgColor: 'var(--status-red-bg)',
      };
    }
    if (dispatchOutcome === 'PRE_PROVIDER_REJECTION') {
      return {
        label: 'Rejected pre-send',
        color: 'var(--status-red-text)',
        bgColor: 'var(--status-red-bg)',
      };
    }
    if (dispatchOutcome === 'UNKNOWN') {
      return {
        label: 'Outcome unknown',
        color: 'var(--status-amber-text)',
        bgColor: 'var(--status-amber-bg)',
      };
    }
    return { label: 'Sent', color: 'var(--accent)', bgColor: 'var(--action-primary-soft)' };
  }

  // Failed states
  if (status === 'FAILED') {
    if (dispatchOutcome === 'UNKNOWN') {
      return {
        label: 'Outcome unknown',
        color: 'var(--status-amber-text)',
        bgColor: 'var(--status-amber-bg)',
      };
    }
    return {
      label: 'Failed',
      color: 'var(--status-red-text)',
      bgColor: 'var(--status-red-bg)',
    };
  }

  // In-flight states
  if (status === 'SENDING') {
    return { label: 'Sending…', color: 'var(--accent)', bgColor: 'var(--action-primary-soft)' };
  }
  if (status === 'QUEUED') {
    return { label: 'Queued', color: 'var(--status-amber-text)', bgColor: 'var(--status-amber-bg)' };
  }
  if (status === 'PENDING') {
    return { label: 'Pending', color: 'var(--muted)', bgColor: 'var(--status-gray-bg)' };
  }

  // Unknown / future values — graceful degradation
  return {
    label: status ? String(status).replace(/_/g, ' ') : 'Unknown',
    color: 'var(--muted)',
    bgColor: 'var(--status-gray-bg)',
  };
}

// ─── Channel Presentation ─────────────────────────────────────────────────────

export interface ChannelTheme {
  label: string;
  color: string;
  bgColor: string;
  /** Unicode icon/emoji for compact inline display */
  icon: string;
}

export function channelTheme(channel: NotificationChannel | null | undefined): ChannelTheme {
  switch (channel) {
    case 'WHATSAPP':
      return { label: 'WhatsApp', color: 'var(--status-green-text)', bgColor: 'var(--status-green-bg)', icon: '💬' };
    case 'FCM':
      return { label: 'FCM Push', color: 'var(--ink-soft)', bgColor: 'var(--action-primary-soft)', icon: '🔔' };
    case 'SMS':
      return { label: 'SMS', color: 'var(--ink-soft)', bgColor: 'var(--action-primary-soft)', icon: '📱' };
    default:
      return { label: channel ?? 'Unknown', color: 'var(--muted)', bgColor: 'var(--status-gray-bg)', icon: '📨' };
  }
}

// ─── Milestone Timestamps ─────────────────────────────────────────────────────

export interface NotificationMilestones {
  sent: string | null;
  delivered: string | null;
  read: string | null;
}

/**
 * Extracts milestone timestamps from a notification item.
 * Do NOT use `readAt` to infer vendor intent — it is a read receipt only.
 */
export function notificationMilestones(item: NotificationHistoryItem): NotificationMilestones {
  return {
    sent: item.sentAt ?? null,
    delivered: item.deliveredAt ?? null,
    read: item.readAt ?? null,
  };
}

// ─── Relative Time ────────────────────────────────────────────────────────────

const LOCALE = 'en-IN';

export function formatAbsoluteTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(LOCALE, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  } catch {
    return iso;
  }
}

// ─── Adaptive Polling Helper ─────────────────────────────────────────────────

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export function calculateHistoryRefetchInterval(items: NotificationHistoryItem[]): number | false {
  if (items.length === 0) {
    return false; // Old/completed/empty page with no eligible records: no automatic timer
  }

  const now = Date.now();

  // 10s while relevant dispatch/work pending
  const hasPending = items.some(
    (it) => it.status === 'PENDING' || it.status === 'QUEUED' || it.status === 'SENDING',
  );
  if (hasPending) {
    return 10_000;
  }

  // 30s for accepted WhatsApp still eligible for receipt updates within 24h of send
  const hasEligibleWhatsAppReceipts = items.some((it) => {
    if (it.channel !== 'WHATSAPP') return false;
    if (it.status === 'READ') return false; // terminal receipt
    if (it.status === 'SENT' || it.status === 'DELIVERED') {
      const sendTimeStr = it.sentAt ?? it.createdAt;
      const sendTime = sendTimeStr ? new Date(sendTimeStr).getTime() : 0;
      return sendTime > 0 && now - sendTime < TWENTY_FOUR_HOURS_MS;
    }
    return false;
  });

  if (hasEligibleWhatsAppReceipts) {
    return 30_000;
  }

  // All records completed, failed, or older than 24h: stop automatic timer
  return false;
}
